import { CognitoUserAttribute, CognitoUserPool } from 'amazon-cognito-identity-js';

const COGNITO_AUTHORITY = process.env.REACT_APP_COGNITO_AUTHORITY;
const COGNITO_CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID;
const COGNITO_USER_POOL_ID = process.env.REACT_APP_COGNITO_USER_POOL_ID || deriveUserPoolId(COGNITO_AUTHORITY);
const PROFILE_CHANGE_STORAGE_PREFIX = 'cognito-profile-updated-at';
const PROFILE_CHANGE_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;

function deriveUserPoolId(authority) {
  if (!authority) {
    return '';
  }

  try {
    const url = new URL(authority);
    return url.pathname.replace(/^\//, '');
  } catch (error) {
    return authority.split('/').pop() || '';
  }
}

export function getUserPool() {
  if (!COGNITO_USER_POOL_ID || !COGNITO_CLIENT_ID) {
    return null;
  }

  return new CognitoUserPool({
    UserPoolId: COGNITO_USER_POOL_ID,
    ClientId: COGNITO_CLIENT_ID,
  });
}

export function getCurrentCognitoUser() {
  const userPool = getUserPool();
  if (!userPool) {
    return null;
  }

  return userPool.getCurrentUser();
}

export function getCurrentCognitoSession() {
  return new Promise((resolve) => {
    const currentUser = getCurrentCognitoUser();

    if (!currentUser) {
      resolve(null);
      return;
    }

    currentUser.getSession((sessionError, session) => {
      if (sessionError || !session?.isValid()) {
        resolve(null);
        return;
      }

      resolve(session);
    });
  });
}

export function getCurrentAuthenticatedUsername() {
  return new Promise((resolve) => {
    const currentUser = getCurrentCognitoUser();

    if (!currentUser) {
      resolve(null);
      return;
    }

    currentUser.getSession((sessionError, session) => {
      if (sessionError || !session?.isValid()) {
        resolve(null);
        return;
      }

      currentUser.getUserAttributes((attributesError, attributes) => {
        if (attributesError || !attributes) {
          resolve(currentUser.getUsername());
          return;
        }

        const preferredUsername = attributes.find((attribute) => attribute.getName() === 'preferred_username')?.getValue();
        resolve(preferredUsername || currentUser.getUsername());
      });
    });
  });
}

export async function getCurrentUserGroups() {
  const session = await getCurrentCognitoSession();
  if (!session) {
    return [];
  }

  const payload = session.getIdToken()?.decodePayload?.() || {};
  const groups = payload['cognito:groups'];

  if (Array.isArray(groups)) {
    return groups;
  }

  return groups ? [groups] : [];
}

export async function getCurrentIdToken() {
  const session = await getCurrentCognitoSession();
  return session?.getIdToken?.().getJwtToken?.() || '';
}

function getAttributeValue(attributes, name) {
  return attributes.find((attribute) => attribute.getName() === name)?.getValue() || '';
}

function getProfileStorageKey(userIdentifier) {
  return `${PROFILE_CHANGE_STORAGE_PREFIX}:${userIdentifier}`;
}

function getLastProfileChangeAt(userIdentifier) {
  if (!userIdentifier || typeof window === 'undefined') {
    return null;
  }

  const storedValue = window.localStorage.getItem(getProfileStorageKey(userIdentifier));
  if (!storedValue) {
    return null;
  }

  const timestamp = Number(storedValue);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function setLastProfileChangeAt(userIdentifier, timestamp = Date.now()) {
  if (!userIdentifier || typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(getProfileStorageKey(userIdentifier), String(timestamp));
}

export function getUsernameChangeCooldownMs() {
  return PROFILE_CHANGE_COOLDOWN_MS;
}

export function getCurrentUserProfile() {
  return new Promise((resolve, reject) => {
    const currentUser = getCurrentCognitoUser();

    if (!currentUser) {
      resolve(null);
      return;
    }

    currentUser.getSession((sessionError, session) => {
      if (sessionError || !session?.isValid()) {
        resolve(null);
        return;
      }

      currentUser.getUserAttributes((attributesError, attributes = []) => {
        if (attributesError) {
          reject(attributesError);
          return;
        }

        const immutableUsername = currentUser.getUsername();
        const userIdentifier = getAttributeValue(attributes, 'sub') || immutableUsername;
        const preferredUsername = getAttributeValue(attributes, 'preferred_username') || immutableUsername;
        const lastProfileChangeAt = getLastProfileChangeAt(userIdentifier);
        const nextProfileChangeAt = lastProfileChangeAt ? lastProfileChangeAt + PROFILE_CHANGE_COOLDOWN_MS : null;

        resolve({
          username: immutableUsername,
          userIdentifier,
          preferredUsername,
          email: getAttributeValue(attributes, 'email'),
          gender: getAttributeValue(attributes, 'gender'),
          phoneNumber: getAttributeValue(attributes, 'phone_number'),
          phoneNumberVerified: getAttributeValue(attributes, 'phone_number_verified') === 'true',
          givenName: getAttributeValue(attributes, 'given_name'),
          familyName: getAttributeValue(attributes, 'family_name'),
          picture: getAttributeValue(attributes, 'picture') || null,
          tags: getAttributeValue(attributes, 'custom:tags') || '',
          lastProfileChangeAt,
          nextProfileChangeAt,
          canChangeProfile: !nextProfileChangeAt || Date.now() >= nextProfileChangeAt,
        });
      });
    });
  });
}

export function updateCurrentUserProfile({ preferredUsername, gender, phoneNumber, email, picture, tags, currentProfile }) {
  const USER_API = (process.env.REACT_APP_USER_API_BASE_URL || '').replace(/\/$/, '');

  if (USER_API) {
    return new Promise(async (resolve, reject) => {
      try {
        const token = await getCurrentIdToken();
        if (!token) {
          reject(new Error('You must be signed in to update your profile.'));
          return;
        }

        const payload = { preferredUsername, gender, phoneNumber, email, picture, tags, currentProfile };

        const res = await fetch(`${USER_API}/user/profile`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          reject(new Error(err?.message || 'Unable to update profile.'));
          return;
        }

        const data = await res.json().catch(() => ({}));
        resolve(data);
      } catch (e) {
        reject(e);
      }
    });
  }

  return new Promise((resolve, reject) => {
    const currentUser = getCurrentCognitoUser();

    if (!currentUser) {
      reject(new Error('You must be signed in to update your profile.'));
      return;
    }

    currentUser.getSession((sessionError, session) => {
      if (sessionError || !session?.isValid()) {
        reject(sessionError || new Error('Your session is no longer valid.'));
        return;
      }

      const attributesToUpdate = [];
      const trimmedPreferredUsername = preferredUsername?.trim() || '';
      const trimmedGender = gender?.trim() || '';
      const trimmedPhoneNumber = phoneNumber?.trim() || '';
      const trimmedEmail = email?.trim() || '';
      const trimmedPicture = picture?.trim() || '';
      const trimmedTags = tags?.trim() || '';
      const normalizedCurrentPreferredUsername = currentProfile?.preferredUsername?.trim() || '';
      const normalizedCurrentGender = currentProfile?.gender?.trim() || '';
      const normalizedCurrentPhoneNumber = currentProfile?.phoneNumber?.trim() || '';
      const normalizedCurrentEmail = currentProfile?.email?.trim() || '';
      const normalizedCurrentPicture = currentProfile?.picture?.trim() || '';
      const normalizedCurrentTags = currentProfile?.tags?.trim() || '';

      // Changes that are rate-limited (once per PROFILE_CHANGE_COOLDOWN_MS)
      const wantsToChangeProfileFields = (
        (trimmedPreferredUsername && trimmedPreferredUsername !== normalizedCurrentPreferredUsername) ||
        (trimmedEmail && trimmedEmail !== normalizedCurrentEmail) ||
        (trimmedPhoneNumber && trimmedPhoneNumber !== normalizedCurrentPhoneNumber)
      );

      if (wantsToChangeProfileFields && !currentProfile?.canChangeProfile) {
        reject(new Error('You can only change your username, email, or phone once every 30 days.'));
        return;
      }

      if (trimmedPreferredUsername && trimmedPreferredUsername !== normalizedCurrentPreferredUsername) {
        attributesToUpdate.push(new CognitoUserAttribute({ Name: 'preferred_username', Value: trimmedPreferredUsername }));
      }

      if (trimmedEmail && trimmedEmail !== normalizedCurrentEmail) {
        attributesToUpdate.push(new CognitoUserAttribute({ Name: 'email', Value: trimmedEmail }));
      }

      if (trimmedGender && trimmedGender !== normalizedCurrentGender) {
        attributesToUpdate.push(new CognitoUserAttribute({ Name: 'gender', Value: trimmedGender }));
      }

      if (trimmedPhoneNumber !== normalizedCurrentPhoneNumber) {
        attributesToUpdate.push(new CognitoUserAttribute({ Name: 'phone_number', Value: trimmedPhoneNumber }));
      }

      if (trimmedPicture && trimmedPicture !== normalizedCurrentPicture) {
        attributesToUpdate.push(new CognitoUserAttribute({ Name: 'picture', Value: trimmedPicture }));
      }

      if (trimmedTags !== normalizedCurrentTags) {
        attributesToUpdate.push(new CognitoUserAttribute({ Name: 'custom:tags', Value: trimmedTags }));
      }

      if (attributesToUpdate.length === 0) {
        resolve({ updated: false });
        return;
      }

      currentUser.updateAttributes(attributesToUpdate, (updateError) => {
        if (updateError) {
          reject(updateError);
          return;
        }

        if (wantsToChangeProfileFields) {
          setLastProfileChangeAt(currentProfile?.userIdentifier || currentUser.getUsername());
        }

        resolve({ updated: true });
      });
    });
  });
}

export function signOutCurrentUser() {
  const currentUser = getCurrentCognitoUser();
  if (!currentUser) {
    return false;
  }

  currentUser.signOut();
  return true;
}
